import { useEffect, useState } from "react"
import { IconSquareArrowUpRightOutline18 as ExternalLinkIcon } from "nucleo-ui-outline-18"

import type { MembershipRole } from "@productlane/db/browser"
import { cn } from "@productlane/lib/src/cn"
import { Button, Dialog } from "@productlane/ui"

import { ROLE_OPTIONS, shouldPersistRoleChange } from "./member-roles"

export function ChangeRoleDialog({
	isOpen,
	onOpenChange,
	memberName,
	currentRole,
	onSave,
	isLoading,
}: {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	memberName: string
	currentRole: MembershipRole
	onSave: (role: MembershipRole) => Promise<void>
	isLoading: boolean
}) {
	const [selectedRole, setSelectedRole] = useState(currentRole)

	useEffect(() => {
		if (isOpen) setSelectedRole(currentRole)
	}, [isOpen, currentRole])

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={`Change ${memberName}'s role`}
			footer={
				<div className="flex w-full items-center justify-between gap-4">
					<a
						href="https://productlane.com/docs/12c19a75-1c72-40b2-ae26-c22300eb3203"
						target="_blank"
						rel="noreferrer"
						className="flex items-center gap-1.5 text-xs text-secondary hover:text-primary"
					>
						Learn more about user roles
						<ExternalLinkIcon className="h-3.5 w-3.5" />
					</a>
					<div className="flex items-center gap-2">
						<Button variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button
							isLoading={isLoading}
							onClick={async () => {
								if (shouldPersistRoleChange(currentRole, selectedRole)) {
									await onSave(selectedRole)
								}
								onOpenChange(false)
							}}
						>
							Save
						</Button>
					</div>
				</div>
			}
		>
			<div className="flex flex-col gap-2 py-1">
				{ROLE_OPTIONS.map((option) => {
					const isSelected = selectedRole === option.role
					return (
						<button
							key={option.role}
							type="button"
							onClick={() => setSelectedRole(option.role)}
							className={cn(
								"flex cursor-default flex-col gap-1 rounded-lg border border-gray-200 p-3.5 text-left transition-colors dark:border-gray-700/80",
								isSelected
									? "border-transparent bg-gray-100 ring-2 ring-inset ring-gray-900 dark:bg-gray-800 dark:ring-gray-100"
									: "hover:bg-gray-100 dark:hover:bg-gray-900",
							)}
						>
							<span className="text-sm font-medium">{option.label}</span>
							<span className="text-sm text-secondary">
								{option.description}
							</span>
						</button>
					)
				})}
			</div>
		</Dialog>
	)
}
